import React, { useEffect, useRef, useCallback } from 'react';
import { useStore, StoreTypes } from '../../../context';
import * as types from '../../../constants/actionTypes';
import { KeyboardType } from 'constants/keyboardType';
import { useConvertJSONToSVG } from 'customHooks/canvas';
import { useCanvasViewTransform } from 'customHooks/canvasViewTransform';
import { useFabricListener } from 'customHooks/fabric/listener';
import { EventBus } from 'events/EventBus';
import { CanvasEvent } from 'events/EventTypes';
import { fabric } from 'fabric';

import { HotKeys } from 'react-hotkeys';
const STEP = 5;

const FabricCanvas = props => {
  const { isDrawingMode, className } = props;
  const [
    {
      fullWidthInfo,
      scale,
      offsetX,
      offsetY,
      objectPointerEventStatus
    }, readerDispatch
  ] = useStore(StoreTypes.reader);
  const [{ books, bookId, style }] = useStore(StoreTypes.books);
  const { width, height } = style;
  const [{ isActive, activeCanvasObject, canvas }, dispatch] = useStore(
    StoreTypes.canvas
  );
  const canvasRef = useRef(null);
  const book = books.find(book => book.bookId === bookId);
  const { convertJSONToSVG } = useConvertJSONToSVG();

  const fabricRef = useRef()

  const viewTransform = useCanvasViewTransform({
    width,
    height,
    scale,
    offsetX,
    offsetY,
    fullWidthScale: fullWidthInfo.scale
  });

  const removeCanvasObjectHandler = useCallback(() => {
    EventBus.emit({ event: CanvasEvent.RemoveCanvasObjectEvent });
  }, []);

  const modifyCanvasObjectHandler = useCallback((object, property, value) => {
    EventBus.emit({
      event: CanvasEvent.ModifyCanvasObjectPropertyEvent,
      payload: { object, property, value }
    });
  }, []);

  const moveSelected = useCallback(
    direction => {
      let property;
      let value;
      if (activeCanvasObject) {
        switch (direction) {
          case KeyboardType.LEFT:
            property = 'left';
            value = activeCanvasObject[property] - STEP;
            break;
          case KeyboardType.UP:
            property = 'top';
            value = activeCanvasObject[property] - STEP;
            break;
          case KeyboardType.RIGHT:
            property = 'left';
            value = activeCanvasObject[property] + STEP;
            break;
          case KeyboardType.DOWN:
            property = 'top';
            value = activeCanvasObject[property] + STEP;
            break;
          default:
            break;
        }
        modifyCanvasObjectHandler(activeCanvasObject, property, value);
      }
    },
    [activeCanvasObject, modifyCanvasObjectHandler]
  );

  useFabricListener('keydown', e => {
    if (e.repeat || e.target.tagName.toLowerCase() === 'input') {
      return;
    }
    const key = e.which || e.keyCode;
    switch (key) {
      case 37:
        moveSelected(KeyboardType.LEFT);
        break;
      case 38:
        moveSelected(KeyboardType.UP);
        break;
      case 39:
        moveSelected(KeyboardType.RIGHT);
        break;
      case 40:
        moveSelected(KeyboardType.DOWN);
        break;
      default:
        break;
    }
  });

  useEffect(() => {
    const isSmoothBrush = localStorage.getItem('smoothBrush') === 'true';
    fabric.devicePixelRatio = isSmoothBrush ? Math.min(window.devicePixelRatio || 1, 2) : (window.devicePixelRatio || 1);

    dispatch({
      type: types.CANVAS_INITIALIZE,
      el: canvasRef.current,
      width,
      height,
      isDrawingMode
    });

    // 關鍵修復：為 canvas 元素設定 touch-action，防止 iOS 手勢識別器干擾
    if (canvasRef.current) {
      canvasRef.current.style.touchAction = 'none';
      // Fabric.js 會創建上層 canvas，也需要設定
      var parentEl = canvasRef.current.parentElement;
      if (parentEl) {
        var upperCanvas = parentEl.querySelector('.upper-canvas');
        if (upperCanvas) {
          upperCanvas.style.touchAction = 'none';
        }
      }
    }
    // eslint-disable-next-line
  }, [bookId]);

  // Recover pointer positions lost when the main thread is busy.
  // The browser coalesces multiple pointermove events into one when JS blocks;
  // getCoalescedEvents() gives back all the intermediate positions.
  // Declared after CANVAS_INITIALIZE effect so it runs after (same bookId dep).
  useEffect(() => {
    const el = canvas.upperCanvasEl;
    if (!el) return;
    const replayCoalescedEvents = (e) => {
      if (!canvas.isDrawingMode || !e.getCoalescedEvents) return;
      const coalesced = e.getCoalescedEvents();
      // Fabric's own listener handles the final event; replay intermediates only
      for (let i = 0; i < coalesced.length - 1; i++) {
        canvas.__onMouseMove(coalesced[i]);
      }
    };
    el.addEventListener('pointermove', replayCoalescedEvents, { capture: true, passive: true });
    return () => el.removeEventListener('pointermove', replayCoalescedEvents, { capture: true });
    // eslint-disable-next-line
  }, [bookId]);

  // 關鍵修復：阻止 iOS WKWebView 的長按選單和手勢干擾
  useEffect(() => {
    if (!fabricRef.current) return;

    var wrapper = fabricRef.current;

    // 防止長按選單
    var preventLongPress = function (e) {
      // 只有在繪圖模式下才阻止默認行為
      if (isActive) {
        e.preventDefault();
      }
    };

    // 防止觸控事件被系統攔截
    var preventGestureStart = function (e) {
      if (isActive) {
        e.preventDefault();
      }
    };

    // 使用 passive: false 確保可以 preventDefault
    wrapper.addEventListener('touchstart', preventLongPress, { passive: false });
    wrapper.addEventListener('touchmove', preventLongPress, { passive: false });
    wrapper.addEventListener('gesturestart', preventGestureStart, { passive: false });
    wrapper.addEventListener('gesturechange', preventGestureStart, { passive: false });

    // 防止 iOS Safari 的選取行為
    wrapper.addEventListener('contextmenu', preventLongPress, { passive: false });

    return function () {
      wrapper.removeEventListener('touchstart', preventLongPress);
      wrapper.removeEventListener('touchmove', preventLongPress);
      wrapper.removeEventListener('gesturestart', preventGestureStart);
      wrapper.removeEventListener('gesturechange', preventGestureStart);
      wrapper.removeEventListener('contextmenu', preventLongPress);
    };
  }, [isActive]);

  useEffect(() => {
    if (book && width > 0 && height > 0) {
      dispatch({
        type: types.CANVAS_RESIZE,
        width,
        height
      });
      convertJSONToSVG({
        keepCanvas: true
      });
    }
    // eslint-disable-next-line
  }, [width, height, book]);

  useEffect(() => {
    if (localStorage.getItem('smoothBrush') !== 'true') return;
    var handleVisibilityChange = function () {
      if (document.visibilityState === 'visible' && width > 0 && height > 0) {
        dispatch({ type: types.CANVAS_RESIZE, width, height });
        convertJSONToSVG({ keepCanvas: true });
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return function () {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [dispatch, width, height, convertJSONToSVG]);

  useEffect(() => {
    console.log(canvasRef.current);
  }, [canvasRef]);

  const keyMap = {
    DELETE: ['del', 'backspace']
  };

  const handlers = {
    DELETE: () => removeCanvasObjectHandler()
  };

  useEffect(() => {
    if (!fabricRef.current) return;

    // console.log((913 * 1.853 - 913) / 2);
    // console.log(-756.62);
    // console.log(-756.62 + (913 * 1.853 - 913) / 2);

    // const fabricW = width
    // const fabricH = height
    // const scaleGapX = ((fabricW) * scale - fabricW) / 2
    // const scaleGapY = (fabricH * scale - fabricH) / 2

    // const newScale = 1 / scale
    // const newOffsetX = -(offsetX * scale + scaleGapX)
    // const newOffsetY = -(offsetY + scaleGapY)
    // const scaleX = (width / fullWidthInfo.scale) * newScale

    // const scaleY = newScale
    // //fabricRef.current.style.transform = `scaleX(${scaleX}) scaleY(${scaleY}) translate(${newOffsetX}px,${newOffsetY}px)`
    // //
    // fabricRef.current.style.transform = `scale(${newScale}) translate(${-scaleX}px,${newOffsetY}px)`

    // var e = window.event;

    // if (!e) return;
    // var posX = e.clientX;
    // var posY = e.clientY;

    // const baseGapX = 22//ReaderView bookMain width:cale(100%-44px)
    // console.log("posX", posX - baseGapX, posY)
    // console.log("baseGapX", baseGapX, scaleGapX, scaleGapY)
    // console.log("offsetX", offsetX);
    // console.log("scaleX", scaleX);
    // console.log(scale, fullWidthInfo.scale, "------", offsetX, offsetY, width, height, newScale);

    // 算式已抽到 useCanvasViewTransform，與雷射筆 overlay 共用同一組補償
    fabricRef.current.style.transform = viewTransform;
  }, [viewTransform, readerDispatch, isActive])

  return (
    <HotKeys keyMap={keyMap} handlers={handlers}>
      <div
        id="FabricElement"
        className={className}
        ref={fabricRef}
        style={{
          display: isActive ? 'block' : 'none'
        }}
      >
        <canvas ref={canvasRef} id="mainCanvas" />
      </div>
    </HotKeys>
  );
};

export default FabricCanvas;
